import { Check, Copy } from 'lucide-react';
import { useRef, useState, type ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@nucleus-commerce/ui/components/button';
import { cn } from '@nucleus-commerce/ui/lib/utils';

/** Shiki-highlighted code fence from a guide, with a copy button. */
export function MdxPre({ className, children, ...props }: ComponentProps<'pre'>) {
  const { t } = useTranslation('docs');
  const ref = useRef<HTMLPreElement>(null);
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(ref.current?.textContent ?? '');
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error(t('mdx.copyFailed'));
    }
  };

  return (
    <div className="group relative my-5">
      <pre
        ref={ref}
        {...props}
        className={cn(
          className,
          'bg-muted/40 overflow-x-auto rounded-lg border p-4 font-mono text-[0.8125rem] leading-6',
          '[&>code]:bg-transparent [&>code]:p-0 [&>code]:text-inherit',
        )}
      >
        {children}
      </pre>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={copied ? t('mdx.copied') : t('mdx.copy')}
        onClick={() => void copy()}
        className="bg-background/80 absolute top-2 right-2 size-7 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
      >
        {copied ? <Check aria-hidden /> : <Copy aria-hidden />}
      </Button>
    </div>
  );
}
